'use client'
// components/FooterBuilder.jsx
// ─────────────────────────────────────────────────────────────────────────────
// A configurable footer for any site: BANDS (full-width rows) → COLUMNS on a
// 12-column grid → WIDGETS. Used when website.footer.layout === 'builder'.
//
// footer = {
//   layout: 'builder',
//   theme: {
//     bgColor ('#1f2937'), bgGradient, bgImageUrl, bgPosition ('center'),
//     overlays: ['<css background>', …]      layers drawn over the image, in order
//     textColor ('#ffffff'), mutedColor ('#9ca3af'), accentColor ('var(--color-main)'),
//     linkColor (textColor), linkHoverColor (accentColor), headingColor (accentColor),
//     borderTop ('3px solid …' | none), fontFamily
//   },
//   bands: [{
//     id, bg (CSS background), paddingTop/Bottom ('2.5rem'), paddingTopMobile/BottomMobile,
//     gap ('2rem'), gapMobile ('2rem'), alignItems ('start'|'center'|'end'), borderTop, fullBleed,
//     columns: [{
//       id, span (desktop ≥1024, of 12), spanMd (≥768), spanMobile (12), maxWidth, align ('start'|'center'),
//       cols / colsMd (lay the column's widgets out in a grid, e.g. 2 link groups side by side), gap,
//       widgets: [ … see below … ]
//     }]
//   }],
//   legal: { show (true), text{lang} ('{year}', '{company}' tokens), company, links [{label{lang}, href}],
//            separator ('|'), layout ('split'|'center'), uppercase, letterSpacing, textColor, linkColor,
//            separatorColor, borderColor, paddingY ('1.5rem'), marginTop ('2.5rem'), fontSize, poweredBy (true) }
// }
//
// Widgets (every widget also takes: id, marginTop, marginBottom, maxWidth, hideOn: 'mobile'|'desktop'):
//   logo     { url, height (48), width (200), alt, link ('/') }
//   text     { text{lang}, size, sizeDesktop, weight, color, lineHeight, letterSpacing, transform }
//   heading  { text{lang}, style ('underline'|'plain'), size, sizeDesktop, weight, color, letterSpacing, transform, borderColor }
//   info     { items [{ icon, text{lang}, href }], size, sizeDesktop, iconColor, color, gap }
//   contacts { items [{ icon, label{lang}, href }], iconStyle ('circle'|'plain'), size, sizeDesktop, iconColor, color, hoverColor }
//   socials  { items [{ platform, url, label }], style ('circle'|'square'|'plain'), size (32), iconColor, hoverBg, hoverColor }
//   links    { title{lang} (drawn as a heading), links [{ label{lang}, href }], columns (1|2), bullet ('chevron'|'dot'|'none'),
//              size, sizeDesktop, color, hoverColor, bulletColor, gap }
//   rule     { width ('3rem'), height ('1.5px'), color }
//   spacer   { size ('1rem') }
//   html     { html{lang} }   trusted admin HTML
//
// Icons: pin, file, phone, email, globe, clock, chat, line, whatsapp, facebook, instagram, tiktok,
//        youtube, linkedin, twitter, threads, wechat, none
// Platforms (socials): facebook, instagram, line, tiktok, youtube, linkedin, twitter, threads, whatsapp, wechat
// Colours can be any CSS colour or a brand variable (var(--color-main), var(--thirdcolor) …).
// ─────────────────────────────────────────────────────────────────────────────
import Image from 'next/image'
import Link from 'next/link'
import LinkComponent from '@components/LinkComponent'
import { FaTiktok, FaFacebook, FaLine, FaYoutube, FaLinkedin, FaWhatsapp, FaWeixin } from 'react-icons/fa'
import { FaSquareThreads, FaXTwitter } from 'react-icons/fa6'

const RTL_LANGS = new Set(['he', 'ar', 'fa', 'ur'])

// ── Icons ────────────────────────────────────────────────────────────────────
const LINE_ICONS = {
  pin:   <><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" /></>,
  file:  <><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></>,
  phone: <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />,
  email: <><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" /><rect x="2" y="4" width="20" height="16" rx="2" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  chat:  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />,
  chevron: <path d="m9 18 6-6-6-6" />,
  instagram: <><rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" x2="17.51" y1="6.5" y2="6.5" /></>,
}
const ALIASES = { website: 'globe', mail: 'email', address: 'pin', location: 'pin', tax: 'file', time: 'clock' }

function Icon({ name, size = 14, style, className }) {
  const n = ALIASES[name] ?? name
  const brand = {
    line: FaLine, facebook: FaFacebook, tiktok: FaTiktok, youtube: FaYoutube, linkedin: FaLinkedin,
    whatsapp: FaWhatsapp, wechat: FaWeixin, twitter: FaXTwitter, threads: FaSquareThreads,
  }[n]
  if (brand) { const B = brand; return <B size={size} style={style} className={className} aria-hidden="true" /> }
  if (!LINE_ICONS[n]) return null
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={style} className={className}>
      {LINE_ICONS[n]}
    </svg>
  )
}

const SOCIAL_LABEL = {
  facebook: 'Facebook', instagram: 'Instagram', line: 'LINE', tiktok: 'TikTok', youtube: 'YouTube',
  linkedin: 'LinkedIn', twitter: 'X', threads: 'Threads', whatsapp: 'WhatsApp', wechat: 'WeChat',
}

// ── Links ────────────────────────────────────────────────────────────────────
const isExternal = (href) => /^(https?:|tel:|mailto:)/.test(href || '')

function FLink({ href, className, style, title, ariaLabel, children }) {
  if (!href || href === '#') return <a href="#" className={className} style={style} title={title} aria-label={ariaLabel}>{children}</a>
  if (isExternal(href)) {
    const web = /^https?:/.test(href)
    return (
      <a href={href} className={className} style={style} title={title} aria-label={ariaLabel}
        target={web ? '_blank' : undefined} rel={web ? 'noopener noreferrer' : undefined}>{children}</a>
    )
  }
  return <LinkComponent href={href} className={className} style={style} title={title} aria-label={ariaLabel}>{children}</LinkComponent>
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const mix = (color, pct) => `color-mix(in srgb, ${color} ${pct}%, transparent)`
// responsive font size via CSS variables (mobile value, then ≥640px value)
const fs = (size, sizeDesktop, fallback) => ({ '--fb-fs': size ?? fallback, '--fb-fs-d': sizeDesktop ?? size ?? fallback })

function widgetBox(w, children, extraClass = '') {
  const hide = w.hideOn === 'mobile' ? ' fb-hide-m' : w.hideOn === 'desktop' ? ' fb-hide-d' : ''
  return (
    <div key={w.id} className={`fb-w${hide}${extraClass ? ` ${extraClass}` : ''}`}
      style={{ marginTop: w.marginTop, marginBottom: w.marginBottom, maxWidth: w.maxWidth }}>
      {children}
    </div>
  )
}

// ── Widgets ──────────────────────────────────────────────────────────────────
function Widget({ w, ctx }) {
  const { ml, t } = ctx

  switch (w.type) {
    case 'logo': {
      if (!w.url) return null
      const img = (
        <Image src={w.url} alt={w.alt || ctx.company || 'Logo'} width={w.width ?? 200} height={w.height ?? 48}
          style={{ objectFit: 'contain', height: w.height ?? 48, width: 'auto', display: 'block' }} />
      )
      return widgetBox(w, w.link === false ? img : <Link href={w.link || '/'} aria-label={`${ctx.company || 'Home'} — Go to homepage`}>{img}</Link>)
    }

    case 'text': {
      const text = ml(w.text)
      if (!text) return null
      return widgetBox(w,
        <p className="fb-fs" style={{
          ...fs(w.size, w.sizeDesktop, '0.875rem'), margin: 0, whiteSpace: 'pre-line',
          color: w.color ?? t.textColor, fontWeight: w.weight ?? 400, lineHeight: w.lineHeight ?? 1.625,
          letterSpacing: w.letterSpacing, textTransform: w.transform,
        }}>{text}</p>)
    }

    case 'heading': {
      const text = ml(w.text)
      if (!text) return null
      return widgetBox(w, <Heading w={w} text={text} t={t} />)
    }

    case 'info': {
      const items = (w.items ?? []).filter(it => ml(it.text))
      if (!items.length) return null
      return widgetBox(w,
        <div style={{ display: 'flex', flexDirection: 'column', gap: w.gap ?? 4 }}>
          {items.map((it, i) => {
            const body = (
              <>
                {it.icon && it.icon !== 'none' && <Icon name={it.icon} size={14} style={{ color: w.iconColor ?? t.accentColor, flexShrink: 0, marginTop: 3 }} />}
                <span>{ml(it.text)}</span>
              </>
            )
            const style = { ...fs(w.size, w.sizeDesktop, '0.75rem'), display: 'flex', alignItems: 'flex-start', gap: 8,
              color: w.color ?? t.textColor, lineHeight: 1.625, textDecoration: 'none' }
            return it.href
              ? <FLink key={it.id ?? i} href={it.href} className="fb-fs fb-hover" style={style}>{body}</FLink>
              : <div key={it.id ?? i} className="fb-fs" style={style}>{body}</div>
          })}
        </div>)
    }

    case 'contacts': {
      const items = (w.items ?? []).filter(it => ml(it.label))
      if (!items.length) return null
      const circle = (w.iconStyle ?? 'circle') === 'circle'
      return widgetBox(w,
        <div style={{ display: 'flex', flexDirection: 'column', gap: w.gap ?? 4 }}>
          {items.map((it, i) => (
            <FLink key={it.id ?? i} href={it.href} className="fb-contact fb-fs" style={{
              ...fs(w.size, w.sizeDesktop, '0.75rem'),
              '--fb-c': w.color ?? t.textColor, '--fb-ch': w.hoverColor ?? t.linkHoverColor, '--fb-ci': w.iconColor ?? t.accentColor,
            }}>
              <span className={circle ? 'fb-ci fb-ci-circle' : 'fb-ci'}><Icon name={it.icon} size={12} /></span>
              <span>{ml(it.label)}</span>
            </FLink>
          ))}
        </div>)
    }

    case 'socials': {
      const items = (w.items ?? []).filter(it => it.platform)
      if (!items.length) return null
      const size = w.size ?? 32
      return widgetBox(w,
        <div style={{ display: 'flex', alignItems: 'center', gap: w.gap ?? 12, flexWrap: 'wrap' }}>
          {items.map((it, i) => {
            const label = ml(it.label) || SOCIAL_LABEL[it.platform] || it.platform
            return (
              <FLink key={it.id ?? i} href={it.url} title={label} ariaLabel={label}
                className={`fb-soc fb-soc-${w.style ?? 'circle'}`}
                style={{ width: size, height: size, '--fb-si': w.iconColor ?? t.accentColor,
                  '--fb-shb': w.hoverBg ?? t.accentColor, '--fb-shc': w.hoverColor ?? t.bgColor }}>
                <Icon name={it.platform} size={Math.round(size * 0.4)} />
              </FLink>
            )
          })}
        </div>)
    }

    case 'links': {
      const links = (w.links ?? []).filter(x => ml(x.label))
      const title = ml(w.title)
      if (!links.length && !title) return null
      const cols   = Math.max(1, Math.min(4, w.columns ?? 1))
      const per    = Math.ceil(links.length / cols)
      const groups = Array.from({ length: cols }, (_, ci) => links.slice(ci * per, ci * per + per))
      const bullet = w.bullet ?? 'chevron'
      return widgetBox(w,
        <>
          {title && <Heading w={{ ...(w.headingStyle ?? {}), style: w.headingStyle?.style ?? 'underline' }} text={title} t={t} />}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, columnGap: '1rem', alignItems: 'start' }}>
            {groups.map((g, gi) => (
              <ul key={gi} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: w.gap ?? '0.5rem' }}>
                {g.map((link, li) => (
                  <li key={link.id ?? li}>
                    <FLink href={link.href} className="fb-link fb-fs" style={{
                      ...fs(w.size, w.sizeDesktop, '0.75rem'),
                      '--fb-c': w.color ?? t.linkColor, '--fb-ch': w.hoverColor ?? t.linkHoverColor,
                      '--fb-b': w.bulletColor ?? mix(t.accentColor, 70), '--fb-bh': w.hoverColor ?? t.accentColor,
                    }}>
                      {bullet === 'chevron' && <Icon name="chevron" size={12} className="fb-bullet" />}
                      {bullet === 'dot' && <span className="fb-bullet fb-dot" aria-hidden="true" />}
                      <span>{ml(link.label)}</span>
                    </FLink>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </>)
    }

    case 'rule':
      return widgetBox(w, <div aria-hidden="true" style={{ width: w.width ?? '3rem', height: w.height ?? '1.5px', background: w.color ?? t.accentColor }} />)

    case 'spacer':
      return widgetBox(w, <div aria-hidden="true" style={{ height: w.size ?? '1rem' }} />)

    case 'html': {
      const html = ml(w.html)
      return html ? widgetBox(w, <div dangerouslySetInnerHTML={{ __html: html }} />) : null
    }

    default:
      return null
  }
}

function Heading({ w, text, t }) {
  const underline = (w.style ?? 'underline') === 'underline'
  const color = w.color ?? t.headingColor
  return (
    <h4 className="fb-fs" style={{
      ...fs(w.size, w.sizeDesktop, '0.75rem'),
      ...(w.size || w.sizeDesktop ? {} : { '--fb-fs-d': '0.875rem' }),
      margin: `0 0 ${w.gapBelow ?? '1.25rem'}`, color, fontWeight: w.weight ?? 500,
      letterSpacing: w.letterSpacing ?? '0.2em', textTransform: w.transform ?? 'uppercase', lineHeight: 1.5,
      paddingBottom: underline ? 6 : 0,
      borderBottom: underline ? `1px solid ${w.borderColor ?? mix(color, 20)}` : 'none',
    }}>{text}</h4>
  )
}

// ── Footer ───────────────────────────────────────────────────────────────────
export default function FooterBuilder({ footer, lang }) {
  if (!footer) return null
  const l   = lang || 'en'
  const ml  = (v) => (v && typeof v === 'object') ? (v[l] || v.en || '') : (v || '')
  const rtl = RTL_LANGS.has(l)

  const th = footer.theme ?? {}
  const t = {
    bgColor:        th.bgColor        ?? '#1f2937',
    textColor:      th.textColor      ?? '#ffffff',
    mutedColor:     th.mutedColor     ?? '#9ca3af',
    accentColor:    th.accentColor    ?? 'var(--color-main)',
  }
  t.linkColor      = th.linkColor      ?? t.textColor
  t.linkHoverColor = th.linkHoverColor ?? t.accentColor
  t.headingColor   = th.headingColor   ?? t.accentColor

  const company = ml(footer.legal?.company) || ''
  const ctx = { ml, t, company }
  const year = new Date().getFullYear()
  const legal = footer.legal ?? {}
  const bands = footer.bands ?? []

  const css = `
    .fb { position: relative; overflow: hidden; }
    .fb-layer { position: absolute; inset: 0; pointer-events: none; }
    .fb-band { position: relative; z-index: 2; }
    .fb-grid { --g: var(--fb-gap-m); display: flex; flex-wrap: wrap; gap: var(--g); align-items: var(--fb-align); }
    .fb-col { flex: 0 0 calc((100% + var(--g)) * var(--fb-span-m) / 12 - var(--g)); min-width: 0; display: flex; flex-direction: column; align-items: var(--fb-col-align); text-align: var(--fb-col-text); }
    .fb-col-grid { display: grid; grid-template-columns: repeat(var(--fb-cols-m), minmax(0, 1fr)); gap: var(--fb-col-gap); align-items: start; }
    .fb-w { min-width: 0; }
    .fb-w ul { min-width: 0; }
    .fb-fs { font-size: var(--fb-fs); }
    .fb-hover { transition: color .2s ease; }
    .fb-hover:hover { color: ${t.linkHoverColor} !important; }
    .fb-contact { display: flex; align-items: center; gap: 12px; color: var(--fb-c); text-decoration: none; letter-spacing: 0.025em; padding: 2px 0; transition: color .2s ease; }
    .fb-contact:hover { color: var(--fb-ch); }
    .fb-ci { display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--fb-ci); transition: all .2s ease; }
    .fb-ci-circle { width: 28px; height: 28px; border-radius: 999px; border: 1px solid color-mix(in srgb, var(--fb-ci) 40%, transparent); }
    .fb-contact:hover .fb-ci-circle { border-color: var(--fb-ci); background: color-mix(in srgb, var(--fb-ci) 10%, transparent); }
    .fb-soc { display: flex; align-items: center; justify-content: center; color: var(--fb-si); transition: all .3s ease; text-decoration: none; }
    .fb-soc-circle, .fb-soc-square { border: 1px solid color-mix(in srgb, var(--fb-si) 40%, transparent); }
    .fb-soc-circle { border-radius: 999px; }
    .fb-soc-square { border-radius: 4px; }
    .fb-soc-circle:hover, .fb-soc-square:hover { background: var(--fb-shb); border-color: var(--fb-shb); color: var(--fb-shc); }
    .fb-soc-plain:hover { opacity: .75; }
    .fb-link { display: flex; align-items: center; gap: 6px; color: var(--fb-c); text-decoration: none; letter-spacing: 0.025em; transition: color .2s ease; }
    .fb-link:hover { color: var(--fb-ch); }
    .fb-bullet { color: var(--fb-b); flex-shrink: 0; transition: all .2s ease; ${rtl ? 'transform: scaleX(-1);' : ''} }
    .fb-link:hover .fb-bullet { color: var(--fb-bh); transform: translateX(${rtl ? '-2px' : '2px'})${rtl ? ' scaleX(-1)' : ''}; }
    .fb-dot { width: 4px; height: 4px; border-radius: 999px; background: var(--fb-b); }
    .fb-legal { display: flex; flex-direction: column; align-items: center; justify-content: space-between; gap: 1rem; }
    .fb-legal a { text-decoration: none; transition: color .2s ease; }
    .fb-legal a:hover { color: ${t.linkHoverColor} !important; }
    .fb-hide-d { display: block; }
    @media (max-width: 767px) { .fb-hide-m { display: none !important; } }
    @media (min-width: 640px) { .fb-fs { font-size: var(--fb-fs-d); } }
    @media (min-width: 768px) {
      .fb-grid { --g: var(--fb-gap); }
      .fb-col { flex-basis: calc((100% + var(--g)) * var(--fb-span-md) / 12 - var(--g)); }
      .fb-col-grid { grid-template-columns: repeat(var(--fb-cols-md), minmax(0, 1fr)); }
      .fb-band { padding-top: var(--fb-pt) !important; padding-bottom: var(--fb-pb) !important; }
      .fb-legal-split { flex-direction: row; }
      .fb-hide-d { display: none !important; }
    }
    @media (min-width: 1024px) {
      .fb-col { flex-basis: calc((100% + var(--g)) * var(--fb-span) / 12 - var(--g)); }
      .fb-col-grid { grid-template-columns: repeat(var(--fb-cols), minmax(0, 1fr)); }
    }
  `

  const bg = th.bgGradient || th.bgColor || t.bgColor

  return (
    <footer dir={rtl ? 'rtl' : 'ltr'} className="fb" style={{
      background: bg, color: t.textColor, textAlign: 'start',
      borderTop: th.borderTop ?? 'none', fontFamily: th.fontFamily,
    }}>
      <style>{css}</style>
      {th.bgImageUrl && (
        <Image src={th.bgImageUrl} alt="" fill sizes="100vw" aria-hidden="true"
          style={{ objectFit: 'cover', objectPosition: th.bgPosition ?? 'center', zIndex: 0 }} />
      )}
      {(th.overlays ?? []).map((o, i) => (
        <div key={i} className="fb-layer" aria-hidden="true" style={{ zIndex: 1, background: o }} />
      ))}

      {bands.map((band, bi) => (
        <div key={band.id ?? bi} className="fb-band" style={{
          background: band.bg, borderTop: band.borderTop,
          paddingTop: band.paddingTopMobile ?? band.paddingTop ?? '2.5rem',
          paddingBottom: band.paddingBottomMobile ?? band.paddingBottom ?? '2.5rem',
          '--fb-pt': band.paddingTop ?? '2.5rem', '--fb-pb': band.paddingBottom ?? '2.5rem',
        }}>
          <div className={band.fullBleed ? undefined : 'container-wide mx-auto'}>
            <div className="fb-grid" style={{
              '--fb-gap': band.gap ?? '2rem', '--fb-gap-m': band.gapMobile ?? band.gap ?? '2rem',
              '--fb-align': ({ start: 'flex-start', center: 'center', end: 'flex-end' })[band.alignItems ?? 'start'] ?? 'flex-start',
            }}>
              {(band.columns ?? []).map((col, ci) => {
                const span = col.span ?? 12
                const center = col.align === 'center'
                const widgets = (col.widgets ?? []).map((w, wi) => <Widget key={w.id ?? wi} w={{ ...w, id: w.id ?? wi }} ctx={ctx} />)
                return (
                  <div key={col.id ?? ci} className="fb-col" style={{
                    '--fb-span': span, '--fb-span-md': col.spanMd ?? span, '--fb-span-m': col.spanMobile ?? 12,
                    '--fb-col-align': center ? 'center' : 'stretch', '--fb-col-text': center ? 'center' : 'start',
                    maxWidth: col.maxWidth, gap: col.cols ? undefined : col.gap,
                  }}>
                    {col.cols ? (
                      <div className="fb-col-grid" style={{
                        '--fb-cols': col.cols, '--fb-cols-md': col.colsMd ?? col.cols, '--fb-cols-m': col.colsMobile ?? 1,
                        '--fb-col-gap': col.gap ?? '2rem',
                      }}>{widgets}</div>
                    ) : widgets}
                  </div>
                )
              })}
            </div>

            {/* Legal row sits inside the last band */}
            {bi === bands.length - 1 && legal.show !== false && (
              <LegalRow legal={legal} ml={ml} t={t} year={year} company={company} />
            )}
          </div>
        </div>
      ))}

      {!bands.length && legal.show !== false && (
        <div className="fb-band container-wide mx-auto"><LegalRow legal={legal} ml={ml} t={t} year={year} company={company} /></div>
      )}
    </footer>
  )
}

function LegalRow({ legal, ml, t, year, company }) {
  const links = (legal.links ?? []).filter(x => ml(x.label))
  const raw   = ml(legal.text)
  const text  = raw ? raw.replace(/\{year\}/g, year).replace(/\{company\}/g, company) : `© ${year}${company ? ` ${company}` : ''}`
  const color = legal.textColor ?? t.mutedColor
  return (
    <div className={`fb-legal${(legal.layout ?? 'split') === 'split' ? ' fb-legal-split' : ''}`} style={{
      marginTop: legal.marginTop ?? '2.5rem', padding: `${legal.paddingY ?? '1.5rem'} 0`,
      borderTop: legal.borderColor === 'none' ? 'none' : `1px solid ${legal.borderColor ?? mix(t.accentColor, 30)}`,
      fontSize: legal.fontSize ?? '0.75rem', color,
      letterSpacing: legal.letterSpacing ?? (legal.uppercase ? '0.15em' : undefined),
      textTransform: legal.uppercase ? 'uppercase' : undefined,
    }}>
      <div style={{ textAlign: 'center' }}>
        {text}
        {legal.poweredBy !== false && (
          <> · Powered by <Link href="https://welist.ai" target="_blank" style={{ color: 'inherit' }}>WeList.ai</Link></>
        )}
      </div>
      {links.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', rowGap: 8 }}>
          {links.map((lk, i) => (
            <span key={lk.id ?? i} style={{ display: 'inline-flex', alignItems: 'center' }}>
              {i > 0 && (
                <span aria-hidden="true" style={{ padding: '0 10px', fontWeight: 300, userSelect: 'none',
                  color: legal.separatorColor ?? mix(t.accentColor, 40) }}>{legal.separator ?? '|'}</span>
              )}
              <FLink href={lk.href} style={{ color: legal.linkColor ?? color, fontWeight: 500 }}>{ml(lk.label)}</FLink>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}